import React from 'react';
import {
  Modal,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from 'react-native';
//import { SafeAreaView } from 'react-native-safe-area-context';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { AGREEMENT_VERSION, AGREEMENT_TEXT } from '../config/userAgreement';
import { getCardMargin } from '../config/configManager';

interface UserAgreementModalProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  version?: string;
  text?: string;
}

export const UserAgreementModal: React.FC<UserAgreementModalProps> = ({
  visible,
  onClose,
  title = 'Terms of Service',
  version = AGREEMENT_VERSION,
  text = AGREEMENT_TEXT,
}) => {
  const isWeb = Platform.OS === 'web';
  const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
  const webModalHeight = Math.max(320, viewportHeight - 32);
  const webModalWidth = Math.max(320, Math.min(1000, viewportWidth - 40));
  const outerStyle = {
    ...styles.outer,
    ...(isWeb ? styles.outerWeb : null),
  };
  const innerStyle = {
    ...styles.inner,
    ...(isWeb ? styles.innerWeb : null),
  };
  const webOverlayStyle = {
    ...styles.webOverlay,
  };
  const webDialogStyle = {
    ...styles.webDialog,
    width: webModalWidth,
    maxWidth: webModalWidth,
    height: webModalHeight,
    maxHeight: webModalHeight,
  };
  const cardStyle = {
    ...styles.card,
    marginHorizontal: getCardMargin(),
    ...(isWeb ? styles.cardWeb : null),
  };
  const footerStyle = {
    ...styles.footer,
    paddingHorizontal: getCardMargin(),
  };

  return (
    <Modal
      visible={visible}
      animationType={isWeb ? 'fade' : 'slide'}
      presentationStyle={isWeb ? 'fullScreen' : 'pageSheet'}
      onRequestClose={onClose}
      transparent={isWeb}
    >
    <SafeAreaProvider>
      {isWeb ? (
        <View style={webOverlayStyle}>
          <View style={webDialogStyle}>
            <View style={styles.pageHeader}>
              <View style={styles.pageHeaderText}>
                <Text style={styles.pageTitle}>{title}</Text>
                <Text style={styles.pageSubtitle}>Version {version}</Text>
              </View>
              <TouchableOpacity
                style={styles.closeBtn}
                onPress={onClose}
                accessibilityLabel="Close terms of service"
                accessibilityRole="button"
              >
                <Text style={styles.closeBtnText}>✕</Text>
              </TouchableOpacity>
            </View>

            <View style={cardStyle}>
              <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
                <Text style={styles.body}>{text}</Text>
              </ScrollView>
            </View>

            <View style={footerStyle}>
              <TouchableOpacity style={styles.closeFooterBtn} onPress={onClose} accessibilityRole="button">
                <Text style={styles.closeFooterBtnText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      ) : (
        <SafeAreaView style={outerStyle}>
          <View style={innerStyle}>

            {/* Page header — matches SettingsView header style */}
            <View style={styles.pageHeader}>
              <View style={styles.pageHeaderText}>
                <Text style={styles.pageTitle}>{title}</Text>
                <Text style={styles.pageSubtitle}>Version {version}</Text>
              </View>
              <TouchableOpacity
                style={styles.closeBtn}
                onPress={onClose}
                accessibilityLabel="Close terms of service"
                accessibilityRole="button"
              >
                <Text style={styles.closeBtnText}>✕</Text>
              </TouchableOpacity>
            </View>

            {/* White card — matches SettingsView section style */}
            <View style={cardStyle}>
              <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
                <Text style={styles.body}>{text}</Text>
              </ScrollView>
            </View>

            {/* Footer close button on blue background */}
            <View style={footerStyle}>
              <TouchableOpacity style={styles.closeFooterBtn} onPress={onClose} accessibilityRole="button">
                <Text style={styles.closeFooterBtnText}>Close</Text>
              </TouchableOpacity>
            </View>

          </View>
        </SafeAreaView>
      )}
      </SafeAreaProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  outer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  outerWeb: {
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
  },
  inner: {
    flex: 1,
    alignSelf: 'center',
    width: '100%',
  },
  innerWeb: {
    flex: 0,
    minHeight: 0,
  },
  webOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
  },
  webDialog: {
    backgroundColor: '#3b82f6',
    borderRadius: 16,
    overflow: 'hidden',
    minHeight: 0,
    alignSelf: 'center',
  },
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 24,
  },
  pageHeaderText: {
    flex: 1,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  pageSubtitle: {
    fontSize: 14,
    color: '#e2e8f0',
    marginTop: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.2)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  closeBtn: {
    padding: 8,
    marginLeft: 8,
    marginTop: 2,
  },
  closeBtnText: {
    fontSize: 22,
    color: '#ffffff',
    fontWeight: '700',
  },
  card: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    overflow: 'hidden',
  },
  cardWeb: {
    minHeight: 0,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 32,
  },
  body: {
    fontSize: 14,
    lineHeight: 22,
    color: '#2d3748',
  },
  footer: {
    paddingVertical: 16,
  },
  closeFooterBtn: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.35)',
  },
  closeFooterBtnText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
});

